"use client"

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { TransactionsContent } from './TransactionsContent'
import { useTransactionReports, ReportSearchParams } from '@/hooks/use-transaction-reports'
import { useCodeValues } from '@/hooks/use-code-values'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Search, Filter, RefreshCw, X, Download } from 'lucide-react'
import { TablePagination } from '@/components/ui/table-pagination'
import { DatePicker } from '@/components/ui/date-picker'
import { formatDateOnly } from '@/lib/adapters/engagement-adapter'
import { escapeLucene } from '@/lib/utils/lucene-utils'

const SORT_ORDER_OPTIONS = [
  { value: 'TransactionId desc', label: 'ID (Highest)' },
  { value: 'TransactionId asc', label: 'ID (Lowest)' },
  { value: 'TransactionNumber desc', label: 'Transaction # (Newest)' },
  { value: 'TransactionNumber asc', label: 'Transaction # (Oldest)' },
  { value: 'TransactionTypeId asc', label: 'Type (A-Z)' },
  { value: 'TransactionTypeId desc', label: 'Type (Z-A)' },
  { value: 'TransactionDate desc, TransactionId desc', label: 'Date (Newest)' },
  { value: 'TransactionDate asc, TransactionId asc', label: 'Date (Oldest)' },
  { value: 'WorkerName asc', label: 'Worker Name (A-Z)' },
  { value: 'WorkerName desc', label: 'Worker Name (Z-A)' },
  { value: 'PayeeName asc', label: 'Payee Name (A-Z)' },
  { value: 'PayeeName desc', label: 'Payee Name (Z-A)' },
  { value: 'FromDate desc, TransactionId desc', label: 'From Date (Newest)' },
  { value: 'FromDate asc, TransactionId asc', label: 'From Date (Oldest)' },
  { value: 'ToDate desc, TransactionId desc', label: 'To Date (Newest)' },
  { value: 'ToDate asc, TransactionId asc', label: 'To Date (Oldest)' },
  { value: 'BillSubtotal desc', label: 'Gross Bill (Highest)' },
  { value: 'BillSubtotal asc', label: 'Gross Bill (Lowest)' },
  { value: 'PaymentSubtotal desc', label: 'Gross Pay (Highest)' },
  { value: 'PaymentSubtotal asc', label: 'Gross Pay (Lowest)' },
]

const DEFAULT_SORT = { field: 'TransactionDate', direction: 'desc' as const }

interface TransactionsSectionProps {
  engagementId?: number
}

export function TransactionsSection({ engagementId }: TransactionsSectionProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<number[]>([])
  const [startDateFilter, setStartDateFilter] = useState<Date | null>(null)
  const [endDateFilter, setEndDateFilter] = useState<Date | null>(null)
  const [workerFilter, setWorkerFilter] = useState<string[]>([])
  const [sortOrder, setSortOrder] = useState<{ field: string; direction: 'asc' | 'desc' }>(DEFAULT_SORT)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)

  // Get code values for transaction header statuses
  const { getCodeValues } = useCodeValues()
  const statusOptions = getCodeValues('trn.CodeTransactionHeaderStatus', false)

  // Toggle sort on a column: same field toggles direction, new field defaults to desc
  const handleSort = useCallback((field: string) => {
    setSortOrder(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'desc' }
    )
  }, [])

  const hasActiveFilters =
    searchTerm !== '' ||
    statusFilter.length > 0 ||
    workerFilter.length > 0 ||
    startDateFilter !== null ||
    endDateFilter !== null ||
    sortOrder.field !== DEFAULT_SORT.field ||
    sortOrder.direction !== DEFAULT_SORT.direction

  // Resolve the OData $orderby string by finding the matching SORT_ORDER_OPTIONS entry
  const resolvedOrderBy = useMemo(() => {
    const match = SORT_ORDER_OPTIONS.find(opt =>
      opt.value.startsWith(`${sortOrder.field} ${sortOrder.direction}`)
    )
    return match?.value ?? `${sortOrder.field} ${sortOrder.direction}`
  }, [sortOrder])

  // Build composite OData $filter and $search by AND-ing all active filters
  const searchParams: ReportSearchParams = useMemo(() => {
    const filters: string[] = []

    if (statusFilter.length > 0) {
      filters.push(`(${statusFilter.map(id => `TransactionHeaderStatusId eq ${id}`).join(' or ')})`)
    }

    if (startDateFilter) {
      filters.push(`ToDate ge ${formatDateOnly(startDateFilter)}`)
    }
    if (endDateFilter) {
      filters.push(`FromDate le ${formatDateOnly(endDateFilter)}`)
    }

    // Build $search: combine free-text search with worker name exact match using Lucene syntax
    const searchParts: string[] = []
    if (searchTerm) searchParts.push(escapeLucene(searchTerm))
    if (workerFilter.length > 0) {
      const workerPart = workerFilter.map(w => `WorkerName:"${escapeLucene(w)}"`).join(' OR ')
      searchParts.push(workerFilter.length === 1 ? workerPart : `(${workerPart})`)
    }

    return {
      search: searchParts.length > 0 ? searchParts.join(' AND ') : undefined,
      filter: filters.length > 0 ? filters.join(' and ') : undefined,
      orderBy: resolvedOrderBy,
    }
  }, [searchTerm, statusFilter, workerFilter, startDateFilter, endDateFilter, resolvedOrderBy])

  const {
    transactions,
    loading,
    error,
    totalCount,
    isExporting,
    exportError,
    uniqueWorkerNames,
    workerNamesLoading,
    refetch,
    exportToCSV
  } = useTransactionReports({
    engagementId,
    autoFetch: true,
    searchParams,
    currentPage,
    pageSize
  })

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, startDateFilter, endDateFilter, workerFilter, resolvedOrderBy])

  const handleSearch = () => {
    if (currentPage === 1) {
      refetch()
    } else {
      setCurrentPage(1)
    }
  }

  const handleExport = () => {
    exportToCSV(searchParams)
  }

  return (
    <Card className="border w-full">
      <CardHeader className="border-b border-slate-200">
        <div className="flex flex-col space-y-4">
          {/* Header Row */}
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl font-semibold text-slate-900">
              All Items ({totalCount})
              {loading && (
                <span className="ml-2 text-sm font-normal text-slate-500">Loading...</span>
              )}
            </CardTitle>
            <div className="flex items-center gap-3">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-slate-100"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
                {loading && (
                  <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              <Button
                onClick={handleSearch}
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="border-slate-300 hover:bg-slate-50"
              >
                <Filter className="mr-2 h-4 w-4" />
                {showFilters ? "Hide Filters" : "Filters"}
              </Button>
              {/* Clear Filters — shown whenever any filter is non-default */}
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('')
                    setStatusFilter([])
                    setStartDateFilter(null)
                    setEndDateFilter(null)
                    setWorkerFilter([])
                    setSortOrder(DEFAULT_SORT)
                  }}
                  className="border-slate-300 hover:bg-slate-50"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear Filters
                </Button>
              )}
              <Button
                onClick={handleExport}
                disabled={isExporting || loading}
                variant="outline"
                size="sm"
                className="border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                title={isExporting ? "Exporting..." : "Export CSV"}
              >
                <Download className={`h-4 w-4 ${isExporting ? 'animate-pulse' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Export Error */}
          {exportError && (
            <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">Export failed: {exportError}</p>
            </div>
          )}

          {/* Advanced Filters — single row: Status, Worker, Start Date, End Date */}
          {showFilters && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Status Filter */}
                <MultiSelect
                  options={statusOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  placeholder="All Statuses"
                />

                {/* Worker Filter */}
                <MultiSelect<string>
                  options={uniqueWorkerNames.map(name => ({ id: name, text: name }))}
                  value={workerFilter}
                  onChange={setWorkerFilter}
                  placeholder={workerNamesLoading ? "Loading workers..." : "All Workers"}
                  disabled={workerNamesLoading}
                />

                {/* Start Date */}
                <DatePicker
                  value={startDateFilter}
                  onChange={setStartDateFilter}
                  placeholder="Start date"
                  maxDate={endDateFilter || undefined}
                />

                {/* End Date */}
                <DatePicker
                  value={endDateFilter}
                  onChange={setEndDateFilter}
                  placeholder="End date"
                  minDate={startDateFilter || undefined}
                />
              </div>
            </div>
          )}
        </div>
      </CardHeader>

      {/* Transactions Table Content */}
      <CardContent className="p-0">
        <TransactionsContent
          transactions={transactions}
          loading={loading}
          error={error}
          sortField={sortOrder.field}
          sortDirection={sortOrder.direction}
          onSort={handleSort}
        />

        {/* Pagination */}
        <TablePagination
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          disabled={loading}
        />
      </CardContent>
    </Card>
  )
}
